import { ReactNativeZoomableView, ZoomableViewEvent } from '@openspacelabs/react-native-zoomable-view';
import React, { useEffect, useState, forwardRef, useCallback, useMemo, useRef, memo, useImperativeHandle } from 'react';
import { View, Text, useWindowDimensions, Platform } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { debounce, initial, uniqueId, update } from 'lodash';
import { GestureHandlerRootView, PanGestureHandler } from 'react-native-gesture-handler';
import GridPattern from '@components/GridPattern';
import { Spinner } from 'tamagui';
import FlowComponent, { FlowComponentHandle } from './FlowComponent';
import { FlowListItem } from './FlowListComponent';
import ActionLinkHandler from './FlowLinkHandler';

interface UserHierarchyProps {
  schemaData: any;
  openAddUserModal: () => void;
  handleSnap: (value: any) => void;
  ref: any;
  isLoading: boolean;
}

interface UserHierarchyHandle {
  zoomDisabled: () => void;
  zoomEnabled: () => void;
  appendPosition: (values: any, i: number) => void;
  moveTo: (x: number, y: number) => void;
  zoomBy: (zoom: number) => void;
}

export type CombinedHandle = UserHierarchyHandle & {
  zoomableViewMethods: ReactNativeZoomableView;
};


const FlowHierarchy = forwardRef<CombinedHandle, UserHierarchyProps>((props, ref) => {
  const { schemaData, openAddUserModal, isLoading } = props;
  const [zoomVar, setZoomVar] = useState<ZoomableViewEvent>({
    offsetX: -500,
    offsetY: -500,
    zoomLevel: 0.4,
    originalHeight: 0,
    originalWidth: 0,
    originalPageX: 0,
    originalPageY: 0
  });
  const [zoom, setZoom] = useState(true);
  const links = useSelector((state: DefaultRootState) => state.actionMaker.present.actions);
  const schemaRef = useRef<FlowComponentHandle>(null);
  const accelerateValue = Math.pow(1, (1 / zoomVar.zoomLevel) || 1.4) || 3;
  const positionHandlerRef = useRef<FlowComponentHandle>(null);
  const zoomRef = useRef<ReactNativeZoomableView>(null);
  const containerRef = useRef(null);
  const gridRef = useRef(null);
  const scaleFactor = 0.2;

  const _appendPosition = useCallback((values, i) => {
    const newValues = {
      ...values,
      id: i,
      width: values.width || 200,
      plotX: values.x,
      x: values.x,
      y: values.y,
      plotY: values.y,
      fullView: values.fullView,
    };
    // Update arrows in the link handler
    positionHandlerRef.current?.setValues?.(newValues);
  }, []);

  const ensureZoomableViewReady = <T extends unknown>(callback: (zoomableView: ReactNativeZoomableView) => T): T | undefined => {
    if (zoomRef.current) {
      return callback(zoomRef.current);
    } else {
      console.warn('ReactNativeZoomableView is not ready yet.');
    }
  };

  useImperativeHandle(ref, () => ({
    zoomDisabled: _zoomDisabled,
    zoomEnabled: _zoomEnabled,
    // centerContent: _centerContent,
    // center: _handleCenter,
    appendPosition: _appendPosition,
    // handleArrowTranslate: _handleArrowTranslate,
    // handleCommitTranslate: _handleCommitTranslate,
    // handleDisableComponent: _handleDisableComponent,
    // handleEnableComponent: _handleEnableComponent,
    // addDepartment: _addDepartment,
    moveTo: (x, y) => zoomRef.current?.moveTo(x, y),
    zoomBy: (zoom) => zoomRef.current?.zoomBy(zoom),
    // Expose all ReactNativeZoomableView methods
    zoomableViewMethods: new Proxy({}, {
      get: (_, methodName: string) =>
        (...args: unknown[]) =>
          ensureZoomableViewReady((zoomableView) =>
            (zoomableView as any)[methodName]?.(...args)
          ),
    }) as ReactNativeZoomableView,
  }));

  const _zoomDisabled = useCallback(() => {
    setZoom(false);
  }, []);

  const _zoomEnabled = useCallback(() => {
    setZoom(true);
  }, []);

  const handleShiftingEnd = useCallback((event, gestureState, zoomableViewEventObject) => {
    // setZoomVar(zoomableViewEventObject);\
    // positionHandlerRef.current?.setZoomProps(zoomableViewEventObject);
    schemaRef.current?.setZoomProps(zoomableViewEventObject);
    // gridRef.current?.setZoomProps(zoomableViewEventObject);
  }, []);

  const memoizedHandleShiftingEnd = debounce(handleShiftingEnd, 300);

  useEffect(() => {
    if (Platform.OS === 'web' && containerRef.current) {
      const handleWheel = (e) => {
        zoomRef.current?.zoomBy(e.deltaY > 0 ? -0.1 : 0.1);
      };

      const element = containerRef.current;
      element.addEventListener('wheel', handleWheel);

      return () => {
        element.removeEventListener('wheel', handleWheel);
      };
    }
  }, []);

  const handleSnap = (value: any) => {
    console.log('snapping', value);
  }

  // const handleLinks = () => {
  //   if (!userList) return setLinks([]);
  //   const newLinks = [];
  //   userList.forEach((user: any) => {
  //     ['bottomLinks', 'topLinks', 'leftLinks', 'rightLinks'].forEach((linkType: string) => {
  //       user[linkType]?.forEach((link: string) => {
  //         newLinks.push({ source: user.user._id, target: link, position: linkType });
  //       });
  //     });
  //   });
  //   setLinks(newLinks);
  // };

  const handleZoomUpdate = useCallback((event, gestureState, zoomableViewEventObject) => {
    gridRef.current?.setZoomProps(zoomableViewEventObject);
  }, [])

  const memoizedHandleZoomUpdate = debounce(handleZoomUpdate, 200);

  return (
    <View ref={containerRef} style={{ flex: 1, top: "-10%" }}>
      <ReactNativeZoomableView
        ref={zoomRef}
        onPanResponderMove={memoizedHandleZoomUpdate}
        maxZoom={5}
        initialZoom={0.5}
        zoomEnabled={zoom}
        zoomStep={6}
        contentHeight={3000}
        contentWidth={3000}
        bindToBorders={true}
        minZoom={0.2}
        initialOffsetX={0}
        initialOffsetY={0}
        style={{ position: 'absolute', padding: 50, height: '100%' }}
      >
        {
          Platform.OS != 'android' &&
          <GridPattern ref={gridRef} key={'userGrid'} zoomVar={zoomVar} />
        }
        <ActionLinkHandler zoomProps={zoomVar} ref={positionHandlerRef} viewWidth={0} />
        <GestureHandlerRootView style={{ position: 'absolute', backgroundColor: 'transparent', overflow: 'visible', top: 250, }}>
          {
            isLoading ? (
              <Spinner size="large" color='rgb(90,192,234)' style={{ top: 0, left: 0, minHeight: 30, minWidth: 300, borderRadius: 20 }} />
            ) : (
              <>
                <FlowComponent
                  portrait={true}
                  key={'dataset'}
                  initialZoom={0.4}
                  schemaData={schemaData}
                  index={0}
                  ref={schemaRef}
                  showArrows={links?.length < 1}
                  positionHandler={value => _appendPosition(value, 0)}
                  onSnap={handleSnap}
                  zoomVars={zoomVar}
                  accelerateValue={accelerateValue * 2 / (zoomVar.zoomLevel * 100)}
                  transform={{ translateX: 0, translateY: 0 }}
                  reverse={false}
                />
                {
                  links?.length > 0 && links?.map((link, i) => (
                    <View style={{ transform: [{ scale: 0.2 }], zIndex: i * -1 }} key={i}>
                      <FlowListItem
                        positionHandler={(value) => _appendPosition(value, i + 1)}
                        zoomRef={zoomRef}
                        zoomProps={zoomVar}
                        showArrow={(i + 1) === links.length}
                        transform={{ translateX: 0, translateY: (i + 1) * 500 + (schemaData?.dataSchema?.length * 20) }}
                        action={link}
                        index={i}
                      />
                    </View>
                  ))
                }
              </>
            )
          }
        </GestureHandlerRootView>
      </ReactNativeZoomableView >
    </View>
  )
});

export default FlowHierarchy;
